import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { authAPI } from '../services/api';
import { getToken, setToken, clearToken, tokenAgeSeconds } from '../services/tokenStore';

export const AuthContext = createContext(null);

const USER_CACHE_KEY = 'user';

// Renew the session token at most this often (on launch / return to the app).
// Each renewal restarts the server-side expiry, so an active reader stays
// signed in indefinitely.
const REFRESH_AFTER_SECONDS = 24 * 60 * 60;

let refreshInFlight = null;

function refreshTokenIfStale() {
  if (refreshInFlight || !getToken()) return refreshInFlight;
  const age = tokenAgeSeconds();
  // Legacy tokens (no iat) are refreshed straight away to get the long expiry.
  if (age !== null && age < REFRESH_AFTER_SECONDS) return null;
  refreshInFlight = authAPI
    .refresh()
    .then((response) => setToken(response.data.access_token))
    .catch(() => {
      /* network blip: keep the current token; a 401 is handled by the interceptor */
    })
    .finally(() => {
      refreshInFlight = null;
    });
  return refreshInFlight;
}

// Last-known user, cached alongside the token so a returning visitor can render
// immediately instead of blocking the whole app on a /auth/me round-trip.
function readCachedUser() {
  try {
    const raw = localStorage.getItem(USER_CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeCachedUser(user) {
  try {
    if (user) localStorage.setItem(USER_CACHE_KEY, JSON.stringify(user));
    else localStorage.removeItem(USER_CACHE_KEY);
  } catch {
    /* ignore serialization / quota errors */
  }
}

export const AuthProvider = ({ children }) => {
  // Hydrate synchronously from the cached identity. A returning user renders
  // right away; /auth/me then revalidates in the background (below).
  const hasToken = !!getToken();
  const cachedUser = hasToken ? readCachedUser() : null;
  const [user, setUser] = useState(cachedUser);
  // Only block render when we hold a token but have no cached identity to show.
  const [loading, setLoading] = useState(hasToken && !cachedUser);

  const logout = useCallback(() => {
    clearToken();
    writeCachedUser(null);
    setUser(null);
  }, []);

  useEffect(() => {
    // Revalidate the token in the background. In the common case the app has
    // already rendered from the cached user, so this no longer gates paint —
    // it just refreshes identity and catches an expired token.
    const token = getToken();
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    authAPI.getCurrentUser()
      .then(response => {
        setUser(response.data);
        writeCachedUser(response.data);
        refreshTokenIfStale();
      })
      .catch(() => {
        // Only a 401 means the token is actually invalid — the axios response
        // interceptor handles that (clears token + fires auth:unauthorized →
        // logout). For network/5xx blips (e.g. a backend cold start) we keep
        // the optimistic session rather than boot a valid user to /login.
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  // Phones keep the app suspended for days rather than relaunching it, so also
  // renew when the reader comes back to it.
  useEffect(() => {
    const handleVisible = () => {
      if (document.visibilityState === 'visible') refreshTokenIfStale();
    };
    document.addEventListener('visibilitychange', handleVisible);
    return () => document.removeEventListener('visibilitychange', handleVisible);
  }, []);

  // Listen for 401 events from the axios interceptor so we can clear auth
  // state without a full page reload
  useEffect(() => {
    const handleUnauthorized = () => logout();
    window.addEventListener('auth:unauthorized', handleUnauthorized);
    return () => window.removeEventListener('auth:unauthorized', handleUnauthorized);
  }, [logout]);

  // Adopt a freshly issued token (sign-in, password reset) and load its user.
  const loginWithToken = useCallback(async (accessToken) => {
    setToken(accessToken);
    const userResponse = await authAPI.getCurrentUser();
    setUser(userResponse.data);
    writeCachedUser(userResponse.data);
  }, []);

  const login = async (username, password) => {
    const response = await authAPI.login(username, password);
    await loginWithToken(response.data.access_token);
  };

  const register = async (username, email, password) => {
    await authAPI.register(username, email, password);
    await login(username, password);
  };

  return (
    <AuthContext.Provider value={{ user, login, loginWithToken, register, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
